import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowLeft, Crown } from "lucide-react";
import { API } from "@/lib/site";

const Shell = ({ children, testid }) => (
  <section
    className="relative flex min-h-screen items-center justify-center overflow-hidden px-5 py-24"
    data-testid={testid}
  >
    <div
      className="pointer-events-none absolute left-1/2 top-1/3 h-[420px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#2563EB]/22 blur-[130px]"
      aria-hidden="true"
    />
    <div className="glass-panel relative mx-auto w-full max-w-xl rounded-[2rem] p-8 text-center sm:p-12">
      {children}
    </div>
  </section>
);

export function PaymentSuccess({ sessionId }) {
  const [state, setState] = useState("verifying");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return undefined;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts >= 15) {
        setState("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <Shell testid="payment-success-page">
      {state === "verifying" && (
        <div data-testid="payment-verifying" role="status">
          <Loader2 className="mx-auto h-14 w-14 animate-spin text-[#00D4FF]" aria-hidden="true" />
          <h1 className="font-display mt-6 text-2xl font-bold text-white sm:text-3xl">Confirming your payment…</h1>
          <p className="mt-3 text-base text-slate-300">This takes just a few seconds.</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-confirmed" role="status">
          <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-400" aria-hidden="true" />
          <h1 className="font-display mt-6 text-2xl font-black text-white sm:text-3xl">
            Welcome aboard, <span className="text-glow-gradient">Founder!</span>
          </h1>
          <p className="mt-4 text-base leading-relaxed text-slate-300">
            Your Founder Plan subscription is active at $2.99/month — locked in for life. A receipt
            is on its way to your inbox, and you'll get first access the moment the app launches.
          </p>
          <a
            href="/"
            data-testid="payment-home-button"
            className="mt-8 inline-flex min-h-[52px] items-center gap-2 rounded-full bg-[#2563EB] px-8 py-3.5 text-base font-bold text-white transition-all duration-300 hover:bg-[#1D4ED8]"
          >
            Back to VoiceLens AI
          </a>
        </div>
      )}
      {state === "pending" && (
        <div data-testid="payment-pending" role="status">
          <Loader2 className="mx-auto h-14 w-14 text-[#00D4FF]" aria-hidden="true" />
          <h1 className="font-display mt-6 text-2xl font-bold text-white sm:text-3xl">Almost there</h1>
          <p className="mt-3 text-base leading-relaxed text-slate-300">
            Your payment was submitted and confirmation is on its way. Check your email for the
            Stripe receipt — your Founder status will show as active shortly.
          </p>
          <a
            href="/"
            data-testid="payment-home-button"
            className="mt-8 inline-flex min-h-[52px] items-center gap-2 rounded-full border border-[#00D4FF]/35 px-8 py-3.5 text-base font-semibold text-slate-100 hover:bg-[#00D4FF]/10"
          >
            Back to VoiceLens AI
          </a>
        </div>
      )}
      {state === "error" && (
        <div data-testid="payment-error" role="alert">
          <XCircle className="mx-auto h-14 w-14 text-rose-400" aria-hidden="true" />
          <h1 className="font-display mt-6 text-2xl font-bold text-white sm:text-3xl">Missing payment session</h1>
          <p className="mt-3 text-base text-slate-300">We couldn't find this checkout session.</p>
          <a
            href="/"
            data-testid="payment-home-button"
            className="mt-8 inline-flex min-h-[52px] items-center gap-2 rounded-full border border-[#00D4FF]/35 px-8 py-3.5 text-base font-semibold text-slate-100 hover:bg-[#00D4FF]/10"
          >
            Back to VoiceLens AI
          </a>
        </div>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell testid="payment-cancel-page">
      <XCircle className="mx-auto h-16 w-16 text-slate-400" aria-hidden="true" />
      <h1 className="font-display mt-6 text-2xl font-black text-white sm:text-3xl">
        Checkout canceled
      </h1>
      <p className="mt-4 text-base leading-relaxed text-slate-300">
        No charge was made. Your Founder spot — and the $2.99 lifetime price — is still waiting
        whenever you're ready.
      </p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
        <a
          href="/#pricing"
          data-testid="payment-retry-button"
          className="inline-flex min-h-[52px] items-center gap-2 rounded-full bg-[#2563EB] px-8 py-3.5 text-base font-bold text-white transition-all duration-300 hover:bg-[#1D4ED8]"
        >
          <Crown className="h-5 w-5" aria-hidden="true" />
          Try Again
        </a>
        <a
          href="/"
          data-testid="payment-cancel-home-button"
          className="inline-flex min-h-[52px] items-center gap-2 rounded-full border border-[#00D4FF]/35 px-8 py-3.5 text-base font-semibold text-slate-100 hover:bg-[#00D4FF]/10"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          Back to Home
        </a>
      </div>
    </Shell>
  );
}
